import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import FinanceShell from "@/components/finance/FinanceShell";
import { formatTRY, formatDate } from "@/components/finance/financeLabels";
import { Plus, Wallet, Building2, CreditCard, ArrowLeftRight, X } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICONS = { cash: Wallet, bank: Building2, virtual_pos: CreditCard, pos: CreditCard, other: Wallet };
const TYPE_LABELS = { cash: "Kasa", bank: "Banka", pos: "POS", virtual_pos: "Sanal POS", other: "Diğer" };

export default function CashAccountsPage() {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [statement, setStatement] = useState(null);
    const [stLoading, setStLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [showTransfer, setShowTransfer] = useState(false);

    const load = async () => {
        setLoading(true);
        try { setAccounts((await api.get("/admin/finance/cash-accounts")).data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openStatement = async (acc) => {
        setSelected(acc); setStLoading(true);
        try { setStatement((await api.get(`/admin/finance/cash-accounts/${acc.id}/statement?limit=200`)).data); }
        finally { setStLoading(false); }
    };

    return (
        <FinanceShell action={
            <div className="flex gap-2">
                <button onClick={() => setShowTransfer(true)} className="px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm font-semibold inline-flex items-center gap-1.5" data-testid="cash-transfer-btn"><ArrowLeftRight className="h-3.5 w-3.5" /> Transfer</button>
                <button onClick={() => setShowCreate(true)} className="px-3 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700 text-sm font-semibold inline-flex items-center gap-1.5" data-testid="cash-add-btn"><Plus className="h-3.5 w-3.5" /> Yeni Kasa/Banka</button>
            </div>
        }>
            {loading ? <div className="py-10 text-center text-slate-500">Yükleniyor...</div> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {accounts.map((a) => {
                        const Icon = TYPE_ICONS[a.type] || Wallet;
                        return (
                            <button key={a.id} onClick={() => openStatement(a)} className="text-left rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 hover:border-sky-500 transition" data-testid={`cash-card-${a.code}`}>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{TYPE_LABELS[a.type] || a.type} · {a.code}</div>
                                        <div className="font-bold text-slate-900 dark:text-white mt-1">{a.name}</div>
                                        {a.iban && <div className="text-[10px] font-mono text-slate-400 mt-1">{a.iban}</div>}
                                    </div>
                                    <Icon className="h-6 w-6 text-sky-500" />
                                </div>
                                <div className="text-2xl font-extrabold mt-3">{formatTRY(a.balance)}</div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Statement modal */}
            {selected && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => { setSelected(null); setStatement(null); }}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{selected.code}</div>
                                <h3 className="text-xl font-bold">{selected.name} — Ekstre</h3>
                            </div>
                            <button onClick={() => { setSelected(null); setStatement(null); }} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="overflow-auto max-h-[calc(90vh-70px)]">
                            {stLoading ? <div className="py-12 text-center text-slate-500">Yükleniyor...</div> : statement && (
                                <table className="w-full text-sm" data-testid="cash-statement">
                                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest font-bold text-slate-500 sticky top-0">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Tarih</th>
                                            <th className="px-3 py-2 text-left">Fiş</th>
                                            <th className="px-3 py-2 text-left">Açıklama</th>
                                            <th className="px-3 py-2 text-right">Giriş</th>
                                            <th className="px-3 py-2 text-right">Çıkış</th>
                                            <th className="px-3 py-2 text-right">Bakiye</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {statement.items.map((it) => (
                                            <tr key={it.id} className={`border-t border-slate-100 dark:border-slate-800/50 ${it.is_void ? "opacity-40 line-through" : ""}`}>
                                                <td className="px-3 py-2 whitespace-nowrap">{formatDate(it.date)}</td>
                                                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{it.entry_no}</td>
                                                <td className="px-3 py-2 max-w-[260px] truncate" title={it.description}>{it.description}</td>
                                                <td className="px-3 py-2 text-right text-emerald-600">{it.direction === "in" ? formatTRY(it.amount_try) : "—"}</td>
                                                <td className="px-3 py-2 text-right text-rose-600">{it.direction === "out" ? formatTRY(it.amount_try) : "—"}</td>
                                                <td className="px-3 py-2 text-right font-bold">{formatTRY(it.running_balance)}</td>
                                            </tr>
                                        ))}
                                        {statement.items.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-500">Henüz hareket yok</td></tr>}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showCreate && <CreateCashModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
            {showTransfer && <TransferModal accounts={accounts} onClose={() => setShowTransfer(false)} onSaved={() => { setShowTransfer(false); load(); }} />}
        </FinanceShell>
    );
}

function CreateCashModal({ onClose, onSaved }) {
    const [form, setForm] = useState({ code: "", name: "", type: "cash", currency: "TRY", iban: "", bank_name: "" });
    const submit = async () => {
        try { await api.post("/admin/finance/cash-accounts", form); toast.success("Kasa oluşturuldu"); onSaved(); }
        catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    };
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full p-6">
                <h3 className="text-lg font-bold mb-4">Yeni Kasa / Banka</h3>
                <div className="space-y-3">
                    <input placeholder="Kod (KASA-TL-02 vb.)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" data-testid="cash-modal-code" />
                    <input placeholder="Ad" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" data-testid="cash-modal-name" />
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm">
                        <option value="cash">Nakit Kasa</option>
                        <option value="bank">Banka Vadesiz</option>
                        <option value="pos">POS</option>
                        <option value="virtual_pos">Sanal POS</option>
                        <option value="other">Diğer</option>
                    </select>
                    {form.type === "bank" && <input placeholder="Banka adı" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />}
                    {form.type === "bank" && <input placeholder="IBAN" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />}
                </div>
                <div className="flex gap-2 justify-end mt-5">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm">İptal</button>
                    <button onClick={submit} className="px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold text-sm" data-testid="cash-modal-save">Kaydet</button>
                </div>
            </div>
        </div>
    );
}

function TransferModal({ accounts, onClose, onSaved }) {
    const [form, setForm] = useState({ from_account_id: "", to_account_id: "", amount: "", date: new Date().toISOString().slice(0, 10), description: "Hesap transferi" });
    const submit = async () => {
        if (!form.from_account_id || !form.to_account_id || form.from_account_id === form.to_account_id) {
            toast.error("Farklı kaynak ve hedef seçin"); return;
        }
        try { await api.post("/admin/finance/cash-accounts/transfer", { ...form, amount: Number(form.amount) }); toast.success("Transfer tamamlandı"); onSaved(); }
        catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    };
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full p-6">
                <h3 className="text-lg font-bold mb-4">Hesap Transferi</h3>
                <div className="space-y-3">
                    <select value={form.from_account_id} onChange={(e) => setForm({ ...form, from_account_id: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" data-testid="transfer-from">
                        <option value="">Kaynak hesap...</option>
                        {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name} ({formatTRY(a.balance)})</option>)}
                    </select>
                    <select value={form.to_account_id} onChange={(e) => setForm({ ...form, to_account_id: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" data-testid="transfer-to">
                        <option value="">Hedef hesap...</option>
                        {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                    </select>
                    <input type="number" min="0.01" step="0.01" placeholder="Tutar (₺)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" data-testid="transfer-amount" />
                    <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                    <input placeholder="Açıklama" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                </div>
                <div className="flex gap-2 justify-end mt-5">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm">İptal</button>
                    <button onClick={submit} className="px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold text-sm" data-testid="transfer-submit">Transfer Et</button>
                </div>
            </div>
        </div>
    );
}
